/**
 * Enums de dominio. Los valores coinciden 1:1 con los enums de Prisma
 * (apps/api/prisma/schema.prisma) para que viajen como strings sin mapeo.
 */
export const LeadOriginType = { PROSPECTO: 'PROSPECTO', CLIENTE: 'CLIENTE' } as const;
export type LeadOriginType = (typeof LeadOriginType)[keyof typeof LeadOriginType];

export const LeadStatus = { ACTIVO: 'ACTIVO', CERRADO: 'CERRADO' } as const;
export type LeadStatus = (typeof LeadStatus)[keyof typeof LeadStatus];

export const ProcessStatus = {
  INTERESADO: 'INTERESADO',
  COMPRADOR: 'COMPRADOR',
  PROPIETARIO: 'PROPIETARIO',
} as const;
export type ProcessStatus = (typeof ProcessStatus)[keyof typeof ProcessStatus];

export const InterestLevel = {
  ALTO: 'ALTO',
  MEDIO: 'MEDIO',
  BAJO: 'BAJO',
  SIN_DEFINIR: 'SIN_DEFINIR',
} as const;
export type InterestLevel = (typeof InterestLevel)[keyof typeof InterestLevel];

/** Tipos de interacción. Los que dependen de reglas del embudo (SE_CITO, VISITA, PROFORMA) son enum, no catálogo. */
export const InteractionType = {
  LLAMADA: 'LLAMADA',
  WHATSAPP: 'WHATSAPP',
  MAIL: 'MAIL',
  SMS: 'SMS',
  REUNION: 'REUNION',
  VISITA: 'VISITA',
  SE_CITO: 'SE_CITO',
  PROFORMA: 'PROFORMA',
  NOTA: 'NOTA',
} as const;
export type InteractionType = (typeof InteractionType)[keyof typeof InteractionType];

/**
 * Tipos que pueden ser una tarea programada (el «siguiente paso» de la regla de oro). Una NOTA no es tarea: el
 * seguimiento la ignora, así que nunca vale como próximo paso (G3, api-seguimiento-05).
 */
export const TASK_INTERACTION_TYPES = [
  InteractionType.LLAMADA,
  InteractionType.WHATSAPP,
  InteractionType.MAIL,
  InteractionType.SMS,
  InteractionType.REUNION,
  InteractionType.VISITA,
  InteractionType.SE_CITO,
  InteractionType.PROFORMA,
] as const;
export type TaskInteractionType = (typeof TASK_INTERACTION_TYPES)[number];

/** Tipos que cuentan como "contacto" (gestión con la persona). NOTA y SE_CITO no lo son. */
export const CONTACT_INTERACTION_TYPES: readonly InteractionType[] = [
  InteractionType.LLAMADA,
  InteractionType.WHATSAPP,
  InteractionType.MAIL,
  InteractionType.SMS,
  InteractionType.REUNION,
];

export const InteractionStatus = {
  PENDIENTE: 'PENDIENTE',
  COMPLETADA: 'COMPLETADA',
  EXPIRADA: 'EXPIRADA',
  CANCELADA: 'CANCELADA',
} as const;
export type InteractionStatus = (typeof InteractionStatus)[keyof typeof InteractionStatus];

export const InteractionOrigin = {
  MANUAL: 'MANUAL',
  AUTOMATICO: 'AUTOMATICO',
  TRANSITO: 'TRANSITO',
  SOLO_PROFORMA: 'SOLO_PROFORMA',
  IMPORTADO: 'IMPORTADO',
  /** Registrada por el bot / multiagente. */
  BOT: 'BOT',
} as const;
export type InteractionOrigin = (typeof InteractionOrigin)[keyof typeof InteractionOrigin];

export const InteractionResult = { EFECTIVO: 'EFECTIVO', NO_EFECTIVO: 'NO_EFECTIVO' } as const;
export type InteractionResult = (typeof InteractionResult)[keyof typeof InteractionResult];

export const QuoteStatus = {
  VIGENTE: 'VIGENTE',
  EXPIRADA: 'EXPIRADA',
  ANULADA: 'ANULADA',
  CONVERTIDA: 'CONVERTIDA',
} as const;
export type QuoteStatus = (typeof QuoteStatus)[keyof typeof QuoteStatus];

export const Currency = { PEN: 'PEN', USD: 'USD' } as const;
export type Currency = (typeof Currency)[keyof typeof Currency];

export const FinancingType = {
  CONTADO: 'CONTADO',
  HIPOTECARIO: 'HIPOTECARIO',
  MIXTO: 'MIXTO',
  CREDITO_DIRECTO: 'CREDITO_DIRECTO',
} as const;
export type FinancingType = (typeof FinancingType)[keyof typeof FinancingType];

/** Modalidad de una cita (SE_CITO): en persona o por videollamada. */
export const AppointmentMode = { PRESENCIAL: 'PRESENCIAL', VIRTUAL: 'VIRTUAL' } as const;
export type AppointmentMode = (typeof AppointmentMode)[keyof typeof AppointmentMode];
export const APPOINTMENT_MODE_LABELS: Record<AppointmentMode, string> = {
  PRESENCIAL: 'Presencial',
  VIRTUAL: 'Virtual',
};

/** Plataforma de una cita virtual. */
export const AppointmentPlatform = {
  ZOOM: 'ZOOM',
  MEET: 'MEET',
  TEAMS: 'TEAMS',
  WHATSAPP: 'WHATSAPP',
} as const;
export type AppointmentPlatform = (typeof AppointmentPlatform)[keyof typeof AppointmentPlatform];
export const APPOINTMENT_PLATFORM_LABELS: Record<AppointmentPlatform, string> = {
  ZOOM: 'Zoom',
  MEET: 'Google Meet',
  TEAMS: 'Microsoft Teams',
  WHATSAPP: 'Videollamada de WhatsApp',
};

export const DealType = {
  SEPARACION: 'SEPARACION',
  VENTA: 'VENTA',
  ANULACION: 'ANULACION',
} as const;
export type DealType = (typeof DealType)[keyof typeof DealType];

export const UnitType = {
  DEPARTAMENTO: 'DEPARTAMENTO',
  CASA: 'CASA',
  LOTE: 'LOTE',
  OFICINA: 'OFICINA',
  LOCAL: 'LOCAL',
  ESTACIONAMIENTO: 'ESTACIONAMIENTO',
  DEPOSITO: 'DEPOSITO',
} as const;
export type UnitType = (typeof UnitType)[keyof typeof UnitType];

export const UnitCommercialStatus = {
  DISPONIBLE: 'DISPONIBLE',
  SEPARADO: 'SEPARADO',
  VENDIDO: 'VENDIDO',
  BLOQUEADO: 'BLOQUEADO',
} as const;
export type UnitCommercialStatus = (typeof UnitCommercialStatus)[keyof typeof UnitCommercialStatus];

export const ConstructionStatus = {
  PREVENTA: 'PREVENTA',
  EN_CONSTRUCCION: 'EN_CONSTRUCCION',
  ENTREGA_INMEDIATA: 'ENTREGA_INMEDIATA',
  ENTREGADO: 'ENTREGADO',
} as const;
export type ConstructionStatus = (typeof ConstructionStatus)[keyof typeof ConstructionStatus];

export const DocumentType = {
  DNI: 'DNI',
  CE: 'CE',
  PASAPORTE: 'PASAPORTE',
  RUC: 'RUC',
  OTRO: 'OTRO',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const Gender = { M: 'M', F: 'F', OTRO: 'OTRO' } as const;
export type Gender = (typeof Gender)[keyof typeof Gender];

export const MaritalStatus = {
  SOLTERO: 'SOLTERO',
  CASADO: 'CASADO',
  CONVIVIENTE: 'CONVIVIENTE',
  DIVORCIADO: 'DIVORCIADO',
  VIUDO: 'VIUDO',
} as const;
export type MaritalStatus = (typeof MaritalStatus)[keyof typeof MaritalStatus];

export const Role = {
  ADMIN: 'ADMIN',
  GERENTE: 'GERENTE',
  JEFE_COMERCIAL: 'JEFE_COMERCIAL',
  COORDINADOR: 'COORDINADOR',
  ASESOR: 'ASESOR',
  MARKETING: 'MARKETING',
  LECTURA: 'LECTURA',
  /** Cuenta de servicio (bot / multiagente): gestiona leads de su alcance vía API key. */
  BOT: 'BOT',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

/** Roles que ven todos los leads de su alcance (los demás solo ven los propios). */
export const WIDE_SCOPE_ROLES: readonly Role[] = [
  Role.ADMIN,
  Role.GERENTE,
  Role.JEFE_COMERCIAL,
  Role.COORDINADOR,
  Role.MARKETING,
  Role.LECTURA,
  Role.BOT,
];

/** Roles que pueden modificar leads e interacciones. */
export const WRITE_ROLES: readonly Role[] = [
  Role.ADMIN,
  Role.GERENTE,
  Role.JEFE_COMERCIAL,
  Role.COORDINADOR,
  Role.ASESOR,
  Role.BOT,
];

/** Roles que pueden asignar / reasignar leads y crear cuentas de servicio. */
export const MANAGER_ROLES: readonly Role[] = [
  Role.ADMIN,
  Role.GERENTE,
  Role.JEFE_COMERCIAL,
  Role.COORDINADOR,
];

/** Roles a los que se les puede asignar un lead (G1): asesores y quienes reparten; nunca marketing, lectura ni bots. */
export const ASSIGNABLE_ROLES: readonly Role[] = [
  Role.ASESOR,
  Role.COORDINADOR,
  Role.JEFE_COMERCIAL,
];

/** Roles que administran la empresa (usuarios, proyectos, catálogos, configuración). */
export const ADMIN_ROLES: readonly Role[] = [Role.ADMIN, Role.GERENTE];

/** Contacto del lead (7.26): nunca contactado, intentado sin éxito o con al menos un contacto efectivo. */
export const ContactState = {
  SIN_CONTACTO: 'SIN_CONTACTO',
  NO_EFECTIVO: 'NO_EFECTIVO',
  EFECTIVO: 'EFECTIVO',
} as const;
export type ContactState = (typeof ContactState)[keyof typeof ContactState];
export const CONTACT_STATE_LABELS: Record<ContactState, string> = {
  SIN_CONTACTO: 'Sin contacto',
  NO_EFECTIVO: 'No efectivo',
  EFECTIVO: 'Efectivo',
};

export const UserKind = { HUMAN: 'HUMAN', BOT: 'BOT' } as const;
export type UserKind = (typeof UserKind)[keyof typeof UserKind];

export const ActorType = { USER: 'USER', BOT: 'BOT', SYSTEM: 'SYSTEM' } as const;
export type ActorType = (typeof ActorType)[keyof typeof ActorType];

/** Función comercial del asesor (según el DW: digital capta y cita; ventas/híbrido cierra en sala). */
export const AdvisorFunction = {
  DIGITAL: 'DIGITAL',
  VENTAS: 'VENTAS',
  HIBRIDO: 'HIBRIDO',
} as const;
export type AdvisorFunction = (typeof AdvisorFunction)[keyof typeof AdvisorFunction];

export const MediumChannel = {
  DIGITAL: 'DIGITAL',
  TRADICIONAL: 'TRADICIONAL',
  CORREDOR: 'CORREDOR',
  FERIAS: 'FERIAS',
} as const;
export type MediumChannel = (typeof MediumChannel)[keyof typeof MediumChannel];

export const SubStatusGroup = {
  NO_GESTIONADO: 'NO_GESTIONADO',
  DUPLICADO: 'DUPLICADO',
  GESTIONADO: 'GESTIONADO',
  OTRO: 'OTRO',
} as const;
export type SubStatusGroup = (typeof SubStatusGroup)[keyof typeof SubStatusGroup];

export const CatalogKind = {
  ENTRY_CHANNEL: 'ENTRY_CHANNEL',
  SUB_STATUS: 'SUB_STATUS',
  DROP_REASON: 'DROP_REASON',
  DESIST_REASON: 'DESIST_REASON',
  NON_EFFECTIVE_REASON: 'NON_EFFECTIVE_REASON',
  UNIT_TYPOLOGY: 'UNIT_TYPOLOGY',
} as const;
export type CatalogKind = (typeof CatalogKind)[keyof typeof CatalogKind];

export const InboundStatus = {
  PENDIENTE: 'PENDIENTE',
  PROCESADO: 'PROCESADO',
  DUPLICADO: 'DUPLICADO',
  ERROR: 'ERROR',
} as const;
export type InboundStatus = (typeof InboundStatus)[keyof typeof InboundStatus];

export const LeadHistoryKind = {
  ESTADO: 'ESTADO',
  SUB_ESTADO: 'SUB_ESTADO',
  ETAPA: 'ETAPA',
  ASESOR: 'ASESOR',
  TIPO_ORIGEN: 'TIPO_ORIGEN',
  DESISTIMIENTO: 'DESISTIMIENTO',
  REAPERTURA: 'REAPERTURA',
  ATENCION: 'ATENCION',
  /** Corrección de datos del cliente desde la ficha o fusión de personas repetidas (G2). */
  DATOS_CLIENTE: 'DATOS_CLIENTE',
} as const;

/** Quién atiende hoy la conversación de un lead sin cita: el bot o un humano (bot en pausa). */
export const AttentionOwner = { BOT: 'BOT', HUMAN: 'HUMAN' } as const;
export type AttentionOwner = (typeof AttentionOwner)[keyof typeof AttentionOwner];

/** Estado de atención del bot (tablero "Atención del bot"); se conserva mientras un humano interviene. */
export const AttentionState = {
  POR_CONTACTAR: 'POR_CONTACTAR',
  EN_CONVERSACION: 'EN_CONVERSACION',
  PRIORIDAD: 'PRIORIDAD',
  SIN_RESPUESTA: 'SIN_RESPUESTA',
  DESCARTE_PROPUESTO: 'DESCARTE_PROPUESTO',
} as const;
export type AttentionState = (typeof AttentionState)[keyof typeof AttentionState];
export type LeadHistoryKind = (typeof LeadHistoryKind)[keyof typeof LeadHistoryKind];

/** Etapas del pipeline (kanban). Distinto de las etapas del embudo de reporte: aquí se refleja "dónde está" el lead hoy. */
export const PipelineStage = {
  NUEVO: 'NUEVO',
  CONTACTADO: 'CONTACTADO',
  CITA: 'CITA',
  VISITA: 'VISITA',
  PROFORMA: 'PROFORMA',
  SEPARACION: 'SEPARACION',
  VENTA: 'VENTA',
  PERDIDO: 'PERDIDO',
} as const;
export type PipelineStage = (typeof PipelineStage)[keyof typeof PipelineStage];

export const PIPELINE_STAGE_ORDER: readonly PipelineStage[] = [
  PipelineStage.NUEVO,
  PipelineStage.CONTACTADO,
  PipelineStage.CITA,
  PipelineStage.VISITA,
  PipelineStage.PROFORMA,
  PipelineStage.SEPARACION,
  PipelineStage.VENTA,
  PipelineStage.PERDIDO,
];

/** Grano de las tablas de agregación (ver docs/business_rules.md §6: el total global no es la suma por medio). */
/** Plazo de compra declarado por el lead (perfil de compra). */
export const PurchaseHorizon = {
  INMEDIATO: 'INMEDIATO',
  MENOS_3_MESES: 'MENOS_3_MESES',
  DE_3_A_6_MESES: 'DE_3_A_6_MESES',
  DE_6_A_12_MESES: 'DE_6_A_12_MESES',
  MAS_12_MESES: 'MAS_12_MESES',
  SIN_DEFINIR: 'SIN_DEFINIR',
} as const;
export type PurchaseHorizon = (typeof PurchaseHorizon)[keyof typeof PurchaseHorizon];

export const PurchasePurpose = {
  VIVIENDA: 'VIVIENDA',
  INVERSION: 'INVERSION',
  SIN_DEFINIR: 'SIN_DEFINIR',
} as const;
export type PurchasePurpose = (typeof PurchasePurpose)[keyof typeof PurchasePurpose];

export const FunnelGrain = {
  PROJECT: 'PROJECT',
  PROJECT_CATEGORY: 'PROJECT_CATEGORY',
  PROJECT_ADVISOR: 'PROJECT_ADVISOR',
  PROJECT_ADVISOR_CATEGORY: 'PROJECT_ADVISOR_CATEGORY',
} as const;
export type FunnelGrain = (typeof FunnelGrain)[keyof typeof FunnelGrain];
